// Phase 0094-P1 (addendum) — Payroll Costing dashboard + 9 reports.

import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Empty, Input, Row, Skeleton, Space, Table,
  Tabs, Tag, Typography,
} from 'antd'
import { ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'

import { payrollApi } from '@/features/payroll/payrollApi'

const { Text, Title } = Typography

function confColor(c: string) {
  if (c === 'EXACT') return 'success'
  if (c === 'ESTIMATED') return 'warning'
  if (c === 'MANUAL') return 'blue'
  return 'error'
}

export default function PayrollCostingPage() {
  const { message } = App.useApp()
  const [runIdInput, setRunIdInput] = useState('')
  const [runId, setRunId] = useState<string | null>(null)

  const dashboard = useQuery({
    queryKey: ['costing-dash', runId],
    queryFn: () => payrollApi.costingDashboard(runId!),
    enabled: !!runId,
  })
  const deptR = useQuery({
    queryKey: ['costing-dept', runId],
    queryFn: () => payrollApi.deptReport(runId!),
    enabled: !!runId,
  })
  const machineR = useQuery({
    queryKey: ['costing-machine', runId],
    queryFn: () => payrollApi.machineReport(runId!),
    enabled: !!runId,
  })
  const kgR = useQuery({
    queryKey: ['costing-kg', runId],
    queryFn: () => payrollApi.labourCostPerKg(runId!),
    enabled: !!runId,
  })
  const otR = useQuery({
    queryKey: ['costing-ot', runId],
    queryFn: () => payrollApi.otReport(runId!),
    enabled: !!runId,
  })
  const woR = useQuery({
    queryKey: ['costing-wo', runId],
    queryFn: () => payrollApi.maintenanceWoReport(runId!),
    enabled: !!runId,
  })
  const ctR = useQuery({
    queryKey: ['costing-contract', runId],
    queryFn: () => payrollApi.contractReport(runId!),
    enabled: !!runId,
  })
  const reviewR = useQuery({
    queryKey: ['costing-review', runId],
    queryFn: () => payrollApi.estimatedReview(runId!),
    enabled: !!runId,
  })

  const regenMut = useMutation({
    mutationFn: () => payrollApi.regenAllocations(runId!),
    onSuccess: (r) => {
      message.success(`Allocated ${r.allocations_created} cost rows — ${r.review_required} need review.`)
      Object.values({ dashboard, deptR, machineR, kgR, otR, woR, ctR, reviewR }).forEach(q => q.refetch())
    },
    onError: (e: Error) => message.error(e.message),
  })

  const markReviewedMut = useMutation({
    mutationFn: (id: string) => payrollApi.markAllocReviewed(id),
    onSuccess: () => {
      message.success('Allocation reviewed.')
      reviewR.refetch()
      dashboard.refetch()
    },
  })

  if (!runId) {
    return (
      <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
        <Title level={2}>Payroll Costing</Title>
        <Text type="secondary">
          Department-, machine-, OT-, and ₹/kg-level labour cost reports from the payroll run.
        </Text>
        <Card style={{ marginTop: 16 }}>
          <Space>
            <Input placeholder="Run ID" value={runIdInput}
                    onChange={(e) => setRunIdInput(e.target.value)}
                    style={{ width: 340 }} />
            <Button type="primary"
                    onClick={() => runIdInput.trim() && setRunId(runIdInput.trim())}>
              Load
            </Button>
          </Space>
        </Card>
      </div>
    )
  }

  const ds = dashboard.data
  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2}>Payroll Costing — Run {runId.slice(0, 8)}</Title>
      <Space style={{ marginBottom: 12 }}>
        <Button onClick={() => setRunId(null)}>Change run</Button>
        <Button icon={<ThunderboltOutlined />}
                onClick={() => regenMut.mutate()}
                loading={regenMut.isPending}>
          Re-generate allocations
        </Button>
        <Button icon={<ReloadOutlined />}
                onClick={() => dashboard.refetch()}>Refresh</Button>
      </Space>

      {dashboard.isLoading ? <Skeleton active /> : ds && (
        <>
          <Row gutter={[12, 12]}>
            <Col span={6}><Card><Text type="secondary">Total Labour Cost</Text>
              <div style={{ fontSize: 26, fontWeight: 700 }}>
                ₹{ds.totals.total_labour_cost.toLocaleString()}</div></Card></Col>
            <Col span={6}><Card><Text type="secondary">Reconciled</Text>
              <div style={{ fontSize: 22, fontWeight: 700,
                              color: ds.totals.reconciled ? '#52c41a' : '#ff4d4f' }}>
                {ds.totals.reconciled ? 'YES' : 'NO'}</div></Card></Col>
            <Col span={6}><Card><Text type="secondary">Review Required</Text>
              <div style={{ fontSize: 26, fontWeight: 700,
                              color: ds.totals.review_required > 0 ? '#faad14' : '#52c41a' }}>
                {ds.totals.review_required}</div></Card></Col>
            <Col span={6}><Card><Text type="secondary">Cost Buckets</Text>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{ds.by_cost_type.length}</div></Card></Col>
          </Row>

          {!ds.totals.reconciled && (
            <Alert
              style={{ marginTop: 12 }}
              type="error"
              showIcon
              message="Allocation totals do not match payroll line totals"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {Object.entries(ds.totals.diffs).map(([k, v]) => (
                    <li key={k}>
                      {k}: line {v.line_total}, allocated {v.alloc_total},
                      diff <Text type="danger">{v.diff}</Text>
                    </li>
                  ))}
                </ul>
              }
            />
          )}

          <Tabs
            style={{ marginTop: 16 }}
            defaultActiveKey="buckets"
            items={[
              { key: 'buckets', label: 'By Cost Bucket', children: (
                <Card>
                  <Table
                    rowKey="cost_type"
                    columns={[
                      { title: 'Cost Type', dataIndex: 'cost_type', key: 'cost_type',
                        render: (s: string) => <code style={{ fontSize: 12 }}>{s}</code> },
                      { title: 'Amount', dataIndex: 'amount', key: 'amount',
                        render: (n: number) => `₹${n.toLocaleString()}` },
                      { title: 'Rows', dataIndex: 'rows', key: 'rows' },
                    ] as never}
                    dataSource={ds.by_cost_type}
                    pagination={false}
                    size="small"
                  />
                </Card>
              ) },

              { key: 'dept', label: 'Department-wise', children: (
                <Card>
                  {deptR.isLoading ? <Skeleton active /> : (deptR.data?.length
                    ? <Table
                        rowKey={(r) => r.department_id ?? 'unassigned'}
                        columns={[
                          { title: 'Department', dataIndex: 'department_name',
                            render: (n: string | null) => n ?? <Tag>Unassigned</Tag> },
                          { title: 'Total', dataIndex: 'total',
                            render: (n: number) => <Text strong>₹{n.toLocaleString()}</Text> },
                          { title: 'Cost Breakdown', dataIndex: 'by_cost_type',
                            render: (m: Record<string, number>) => (
                              <Space wrap size={4}>
                                {Object.entries(m).map(([k, v]) => (
                                  <Tag key={k} color="blue">{k.replace(/_LABOUR$|_COST$/, '')}: ₹{v.toLocaleString()}</Tag>
                                ))}
                              </Space>
                            ) },
                        ] as never}
                        dataSource={deptR.data ?? []}
                        size="small"
                        pagination={false}
                      />
                    : <Empty />)}
                </Card>
              ) },

              { key: 'machine', label: 'Machine-wise', children: (
                <Card>
                  {machineR.isLoading ? <Skeleton active /> : (machineR.data?.length
                    ? <Table
                        rowKey="machine_id"
                        columns={[
                          { title: 'Machine', dataIndex: 'machine_code' },
                          { title: 'Name', dataIndex: 'machine_name' },
                          { title: 'Labour Cost', dataIndex: 'labour_cost',
                            render: (n: number) => `₹${n.toLocaleString()}` },
                        ] as never}
                        dataSource={machineR.data ?? []}
                        size="small"
                        pagination={false}
                      />
                    : <Empty description="No machine-level allocations (no worker allocations link to machines)." />)}
                </Card>
              ) },

              { key: 'kg', label: '₹ / kg', children: (
                <Card>
                  {kgR.isLoading ? <Skeleton active /> : (kgR.data?.length
                    ? <Table
                        rowKey="machine_id"
                        columns={[
                          { title: 'Machine', dataIndex: 'machine_code' },
                          { title: 'Labour Cost', dataIndex: 'labour_cost',
                            render: (n: number) => `₹${n.toLocaleString()}` },
                          { title: 'Production kg', dataIndex: 'production_kg',
                            render: (n: number | null) =>
                              n === null ? <Tag color="warning">N/A</Tag> : n.toLocaleString() },
                          { title: '₹ / kg', dataIndex: 'rs_per_kg',
                            render: (n: number | null) =>
                              n === null ? <Tag color="warning">DATA_NOT_AVAILABLE</Tag>
                                          : <Text strong>₹{n}</Text> },
                          { title: 'Confidence', dataIndex: 'calculation_confidence',
                            render: (c: string) => <Tag color={confColor(c)}>{c}</Tag> },
                        ] as never}
                        dataSource={kgR.data ?? []}
                        size="small"
                        pagination={false}
                      />
                    : <Empty />)}
                </Card>
              ) },

              { key: 'ot', label: 'OT', children: (
                <Card>
                  <Table
                    rowKey={(r, i) => `${r.department_id}-${r.machine_id ?? 'na'}-${i}`}
                    columns={[
                      { title: 'Department', dataIndex: 'department_name',
                        render: (s: string | null) => s ?? '—' },
                      { title: 'Machine', dataIndex: 'machine_code',
                        render: (s: string | null) => s ?? '—' },
                      { title: 'Amount', dataIndex: 'amount',
                        render: (n: number) => `₹${n.toLocaleString()}` },
                    ] as never}
                    dataSource={otR.data ?? []}
                    size="small"
                    pagination={false}
                  />
                </Card>
              ) },

              { key: 'wo', label: 'Maintenance WO', children: (
                <Card>
                  <Table
                    rowKey={(r, i) => r.work_order_id ?? `na-${i}`}
                    columns={[
                      { title: 'Work Order', dataIndex: 'work_order_id',
                        render: (s: string | null) => s ?
                          <code style={{ fontSize: 12 }}>{s.slice(0, 8)}</code>
                          : <Tag>No WO link</Tag> },
                      { title: 'Amount', dataIndex: 'amount',
                        render: (n: number) => `₹${n.toLocaleString()}` },
                      { title: 'Rows', dataIndex: 'rows' },
                    ] as never}
                    dataSource={woR.data ?? []}
                    size="small"
                    pagination={false}
                  />
                </Card>
              ) },

              { key: 'contract', label: 'Contract Labour', children: (
                <Card>
                  <Table
                    rowKey={(r, i) => r.department_id ?? `na-${i}`}
                    columns={[
                      { title: 'Department', dataIndex: 'department_name' },
                      { title: 'Amount', dataIndex: 'amount',
                        render: (n: number) => `₹${n.toLocaleString()}` },
                      { title: 'Rows', dataIndex: 'rows' },
                    ] as never}
                    dataSource={ctR.data ?? []}
                    size="small"
                    pagination={false}
                  />
                </Card>
              ) },

              { key: 'review', label: 'Estimated — Review', children: (
                <Card>
                  <Table
                    rowKey="id"
                    columns={[
                      { title: 'Employee', dataIndex: 'employee_name' },
                      { title: 'Code', dataIndex: 'employee_code' },
                      { title: 'Dept', dataIndex: 'department_name' },
                      { title: 'Head', dataIndex: 'head_code' },
                      { title: 'Bucket', dataIndex: 'cost_type',
                        render: (s: string) => <Tag>{s}</Tag> },
                      { title: 'Amount', dataIndex: 'allocated_amount',
                        render: (n: number) => `₹${n.toLocaleString()}` },
                      { title: 'Method', dataIndex: 'allocation_method' },
                      { title: 'Remarks', dataIndex: 'remarks',
                        render: (s: string | null) => s ?? '' },
                      { title: '', key: 'mark',
                        render: (_: unknown, r: { id: string }) => (
                          <Button size="small"
                                  onClick={() => markReviewedMut.mutate(r.id)}>
                            Mark reviewed
                          </Button>
                        ) },
                    ] as never}
                    dataSource={reviewR.data ?? []}
                    size="small"
                    pagination={{ pageSize: 50 }}
                  />
                </Card>
              ) },
            ]}
          />
        </>
      )}
    </div>
  )
}
